import React, { useCallback } from 'react';
import { isEuEnv } from '@mb/lib';
import { Divider } from '@mui/material';
import { useFiltersContext } from '@productSearch/contexts';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { FilterConfig } from './renderer.types';
import { useFilters } from './useFilters';
import { FilterItem } from '../../../components/Filter/FilterItem';
import { searchActionsEnum } from '../../../config/constants';
import { definedTranslation } from '../../../i18n';

import { searchOptionsSelector } from '../../../redux/selectors/searchSelector';
import {
  addFilter,
  pushMobileActiveFilters,
  removeFilter,
} from '../../../redux/sliceCreators/filtersSlice';
import { setSearchOptions } from '../../../redux/sliceCreators/searchSlice';
import { FilterData } from '../../../types';

type FiltersRendererProps = {
  filters: FilterData[];
  isMobile?: boolean;
  onCollapseChange?: () => void;
};

export function FiltersRenderer({
  filters,
  isMobile,
  onCollapseChange,
}: FiltersRendererProps) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const searchOptions = useSelector(searchOptionsSelector);

  const {
    filterListByRenderer,
    currentFilter,
    appFilters,
    mobileActiveFilter,
  } = useFilters(filters);

  const { getFiltersByField, getFilterById, getFiltersByPropertyValue } =
    useFiltersContext();

  const handleRemoveFilter = useCallback(
    (value: FilterData) => {
      if (searchOptions?.action === searchActionsEnum.searchBar) {
        dispatch(
          setSearchOptions({
            ...searchOptions,
            action: searchActionsEnum.filter,
          }),
        );
      }
      dispatch(removeFilter(value));
    },
    [dispatch, searchOptions],
  );

  const handleAddFilter = useCallback(
    (value: FilterData) => {
      dispatch(addFilter(value));
    },
    [dispatch],
  );

  const handleMobileOpenFilter = useCallback(
    (value: FilterData) => {
      dispatch(pushMobileActiveFilters(value));
    },
    [dispatch],
  );

  if (isMobile && mobileActiveFilter) {
    return (
      <FilterItem
        isMobile
        data={currentFilter ?? mobileActiveFilter}
        appFilters={appFilters}
        getFiltersByField={getFiltersByField}
        removeFilter={handleRemoveFilter}
        getFilterById={getFilterById}
        getFiltersByPropertyValue={getFiltersByPropertyValue}
        addFilter={handleAddFilter}
        onCollapseChange={onCollapseChange}
        mobileActiveFilter={currentFilter}
        onMobileOpenFilter={handleMobileOpenFilter}
      />
    );
  }

  const labels = {
    searchPlaceholder: intl.formatMessage(
      definedTranslation.filters.searchPlaceholder,
    ),
  };

  return (
    <div className="flex flex-col gap-2">
      {filterListByRenderer.default.map((filter: FilterConfig) => {
        //don't render filters without any values
        if ((filter?.filterData?.values?.length ?? 0) < 1) {
          return null;
        }

        const id = `${filter?.filterData?.field}-${filter?.filterData?.label}`;

        return (
          <FilterItem
            key={id}
            id={id}
            isMobile={isMobile}
            data={filter?.filterData}
            appFilters={appFilters}
            mobileActiveFilter={mobileActiveFilter}
            searchPlaceholderText={labels.searchPlaceholder}
            getFiltersByField={getFiltersByField}
            removeFilter={handleRemoveFilter}
            getFilterById={getFilterById}
            getFiltersByPropertyValue={getFiltersByPropertyValue}
            addFilter={handleAddFilter}
            onCollapseChange={onCollapseChange}
            onMobileOpenFilter={handleMobileOpenFilter}
          />
        );
      })}
      {!isEuEnv && filterListByRenderer.custom.length > 0 ? (
        <div className="flex flex-col gap-2">
          <Divider sx={dividerSx} />

          {filterListByRenderer.custom.map((filter: FilterConfig) => {
            //don't render filters without any values
            if ((filter?.filterData?.values?.length ?? 0) < 1) {
              return null;
            }

            const id = `${filter?.filterData?.field}-${filter?.filterData?.label}`;

            return (
              <FilterItem
                key={id}
                id={id}
                isMobile={isMobile}
                icon={filter?.icon}
                data={filter?.filterData}
                appFilters={appFilters}
                mobileActiveFilter={mobileActiveFilter}
                searchPlaceholderText={labels.searchPlaceholder}
                getFiltersByField={getFiltersByField}
                removeFilter={handleRemoveFilter}
                getFilterById={getFilterById}
                getFiltersByPropertyValue={getFiltersByPropertyValue}
                addFilter={handleAddFilter}
                onCollapseChange={onCollapseChange}
                onMobileOpenFilter={handleMobileOpenFilter}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

////
const dividerSx = {
  width: '80%',
};
